import { LoaderFunction } from 'react-router-dom';
import { take } from 'rxjs';
import { GridSortModel } from '@mui/x-data-grid-premium';

import { responseHandler } from '../../../../shared/responseHandler/responseHandler';
import {
  defaultProductionFilter as filter,
  IDeliveryNotesTabDataRes,
  initPrintJobsDetailsSortModel,
  initPrintJobsDetailsState,
  IPrintJobsDetailsState,
  IProductionTabDataRes,
  PrintJobsTabs,
} from '../../printJobs/states/printJobsDetails.state';
import { storageHelper } from '../../../../shared/helpers/storage';
import { FullPrintJobsDataRes, printJobsService } from '../../services/printJobs.service';
import { companyConfigService } from '../../../../shared/services/companyConfig/companyConfig.service.ts';

export const storageFilter: IProductionTabDataRes['filter'] | null = storageHelper.session.getItem(
  'printJobs.productionFilterValue',
);
export const storageProductionSortModel: GridSortModel | null = storageHelper.local.getItem(
  'printJobs.productionSortModel',
);
export const storagePrintDeliveryNotesSortModel: GridSortModel | null = storageHelper.local.getItem(
  'printJobs.printDeliveryNotesSortModel',
);

export const printJobsLoader: LoaderFunction = async ({ params }): Promise<IPrintJobsLoader> => {
  const tab = params.tab as PrintJobsTabs;
  const canWorkWithVersionsInProduction =
    !!companyConfigService.getCachedConfigs()?.canWorkWithVersionsInProduction;
  // Deep copy, not `{ ...initPrintJobsDetailsState }`: the shallow spread shares
  // the nested `dataTabs` object with the module-level literal, and this loader
  // writes the fetched payload straight into it. That made every visit inherit
  // the *other* tab's payload from the previous visit — so a delivery-note list
  // fetched before an order was deleted kept being shown afterwards, because
  // `dataForSelectedTab` skips the fetch when the tab already holds data
  // (WA-3769).
  let initPrintJobsDetailsData = structuredClone(initPrintJobsDetailsState);
  initPrintJobsDetailsData.action = 'loader';
  initPrintJobsDetailsData = await resolveDataForTabAndIncomingOrders(
    initPrintJobsDetailsData,
    tab,
  );
  return { initPrintJobsDetailsData, canWorkWithVersionsInProduction };
};

async function resolveDataForTabAndIncomingOrders(
  data: IPrintJobsDetailsState,
  selectedTab: PrintJobsTabs,
): Promise<IPrintJobsDetailsState> {
  if (!Object.values(PrintJobsTabs).includes(selectedTab)) {
    location.href = '/404';
  }
  const sortModel =
    (selectedTab === PrintJobsTabs.production
      ? storageProductionSortModel
      : storagePrintDeliveryNotesSortModel) ||
    initPrintJobsDetailsSortModel(selectedTab === PrintJobsTabs.production ? 'name' : 'orderNo');
  const { nextDeliveryDate } = (await companyConfigService.getConfigsRaw()) || {};
  // A local copy — assigning onto the imported `defaultProductionFilter` would
  // mutate a module-level literal that is also spread into every later
  // production-tab payload, leaking one visit's dates into the next (WA-3769).
  const productionFilter = {
    ...filter,
    fromDate: nextDeliveryDate,
    toDate: nextDeliveryDate,
  };
  data.datePeriod = {
    fromDate: nextDeliveryDate,
    toDate: nextDeliveryDate,
  };
  const { dataTab, statusList } = await printJobsService.globHelpers.streamToPromise(
    printJobsService.sub.fullPrintJobsDataDependedOnSelectedTab().pipe(
      take(1),
      responseHandler<FullPrintJobsDataRes>({
        errorReturnType: getDataStubInCaseOfError(selectedTab),
      }),
    ),
    () =>
      printJobsService.pub.fullPrintJobsDataDependedOnSelectedTab({
        selectedTab,
        datePeriod: data?.datePeriod,
        filter: productionFilter,
      }),
  );
  data.selectedTab = selectedTab;
  data.statusList = statusList;
  data.dataTabs[selectedTab] = {
    ...dataTab,
    params: { sortModel, search: '' },
    loadedDatePeriod: { ...data.datePeriod },
    ...(selectedTab === PrintJobsTabs.production && { filter: productionFilter }),
    selectedRecord: undefined,
  } as IProductionTabDataRes | IDeliveryNotesTabDataRes;
  return data || {};
}

export const getDataStubInCaseOfError = (selectedTab: PrintJobsTabs): FullPrintJobsDataRes => {
  if (selectedTab === PrintJobsTabs.production) {
    return {
      dataTab: {
        productionList: [],
        countOrders: 0,
        countOrderPreProduction: 0,
      },
      statusList: {
        bakery2b: false,
        CAdirect: false,
      },
    };
  }

  // case PrintJobsTabs.printDeliveryNotes
  return {
    dataTab: {
      printDeliveryNotesList: [],
    },
    statusList: {
      bakery2b: false,
      CAdirect: false,
    },
  };
};

export interface IPrintJobsLoader {
  initPrintJobsDetailsData: IPrintJobsDetailsState;
  canWorkWithVersionsInProduction: boolean;
}
